import React, {useMemo} from "react";
import moment from "moment";
import {AutoVisit, Cargo} from "~/apps/packages/auto-visits/queries";
import {
    Grid,
    Card,
    CardContent,
    Typography,
    Divider
} from "@mui/material";
import {AutoVisitStatusBadge} from "~/apps/packages/auto-visits/components/AutoVisitStatusBadge";
import {ShareAutoVisitButton} from "~/apps/packages/auto-visits/components/ShareAutoVisitButton";
import {VehicleNumber} from "~/apps/core/ui/VehicleNumber";
import {AutoVisitCargoCard} from "~/apps/packages/auto-visits/components/AutoVisitCargoCard";
import {formatDate} from "~/apps/core/utils";


export type AutoVisitDetailProps = {
    autoVisit: AutoVisit
}


const TypographyWithLabel = (props: { title: string, children: any }) => (
    <>
        <Typography variant="inputTitle2" color="textSecondary" style={{paddingBottom: 4}}>
            {props.title}
        </Typography>
        <br/>
        <Typography variant="inputText2" color="primary" noWrap display="block">
            {props.children}
        </Typography>
    </>
)


export const AutoVisitDetail = (props: AutoVisitDetailProps) => {
    const {autoVisit} = props;

    const cargoListWithServiceList = useMemo(() => {
        const servicesByCargoID = new Map<Cargo["ID"], AutoVisit["ServiceList"]>();
        for (const item of autoVisit.ServiceList) {
            if (!servicesByCargoID.has(item.CargoID)) {
                servicesByCargoID.set(item.CargoID, []);
            }
            servicesByCargoID.get(item.CargoID).push(item);
        }

        return autoVisit.CargoList.map(item => ({
            ...item,
            ServiceList: servicesByCargoID.get(item.CargoID) || []
        })).sort((a, b) => Number(a.Canceled) - Number(b.Canceled));
    }, [autoVisit]);

    return (
        <Grid container direction="column" rowSpacing="8px" wrap="nowrap">
            {autoVisit?.PIN && (
                <Grid item>
                    <ShareAutoVisitButton autoVisit={autoVisit}/>
                </Grid>
            )}
            <Grid item>
                <Card>
                    <CardContent>
                        <Grid container direction="column" wrap="nowrap" rowSpacing="8px">
                            <Grid item container direction="row" justifyContent="space-between">
                                <Grid item>
                                    <AutoVisitStatusBadge>
                                        {autoVisit.status}
                                    </AutoVisitStatusBadge>
                                </Grid>
                                <Grid item>
                                    <Typography variant="cardText">
                                        {formatDate(moment(autoVisit.Date, "YYYY-MM-DD"))}
                                    </Typography>
                                </Grid>
                            </Grid>
                            <Grid item>
                                <VehicleNumber number={autoVisit.Vehicle.ID}/>
                            </Grid>
                            <Grid item>
                                <Divider/>
                            </Grid>
                            <Grid item>
                                <TypographyWithLabel title="Водитель">
                                    {autoVisit.Driver.Name}
                                </TypographyWithLabel>
                            </Grid>
                            <Grid item>
                                <TypographyWithLabel title="Временной слот">
                                    {autoVisit.TimeSlot.StartTime.substr(0, 5)}&ndash;{autoVisit.TimeSlot.EndTime.substr(0, 5)}
                                </TypographyWithLabel>
                            </Grid>
                            <Grid item>
                                <TypographyWithLabel title="КПП">
                                    {autoVisit.CheckPoint.Name}
                                </TypographyWithLabel>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </Grid>
            <Grid item>
                <Grid container justifyContent="space-between" padding="8px 0">
                    <Typography variant="screenText" color="primary">
                        Грузы
                    </Typography>
                    <Typography variant="screenText" color="additionalSecondary.main">
                        Всего: {cargoListWithServiceList.length}
                    </Typography>
                </Grid>
            </Grid>
            {cargoListWithServiceList.map(cargo => (
                <Grid item key={cargo.ID}>
                    <AutoVisitCargoCard cargo={cargo} editable={false}/>
                </Grid>
            ))}
        </Grid>
    );
}